import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  ActivateCompanyEnum,
  ActivationPageEnum
} from 'core/enum/activationPage';
// import CompanyReady from 'app/companyFinishStep';
import { IMe } from 'core/interface/me';
import ActivationWelcome from 'components/feature/activation/welcome/Index';
import useLocales from 'hooks/useLocales';
import { AuthService } from 'services/auth/auth';
import { AccountServce } from 'services/upload/file-upload';
import ReadyToStart from 'components/feature/activation/readyToStart';
import WhoYouAre from 'components/feature/activation/whoYouAre/Index';
import MatchPreferences from 'components/feature/activation/preferences/Index';
import Education from 'components/feature/activation/education/Index';
import Congratulation from 'components/feature/activation/congratulation/Index';
import EducationView from 'components/feature/activation/education-view/Index';
import ActivationCompanyLogo from 'components/feature/activation/companyLogo';
import CompanyInfo from 'components/feature/activation/companyInfo/Index';
import CompanyInfoSecond from 'components/feature/activation/companyInfoSecond/Index';
import ActivationAvatarLogo from 'components/feature/activation/avatarLogo';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import ActivationCultureProfile from 'components/feature/activation/activationCultureProfile/Index';
import { JobService } from 'services/jobs/job';
import RecruiterSettingsStep from 'components/feature/activation/recruiterSettings/Index';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';
import { TEST_ACTIVATION_ROUTE } from 'core/constants/activationTest';
import { useActivationTestMode } from 'core/context/ActivationTestMode';
import { ActivationFlowProvider } from 'core/context/ActivationFlow';
import PageHeader from 'components/PageHeader';
import TestActivationBanner from 'app/testActivation/Banner';
import TestPayloadPreview from 'app/testActivation/PayloadPreview';
import { normalizeIndustry } from 'utils/industry';

import './Index.scss';

const getStepInfo = (link: string, isRecruiter: boolean) => {
  const recruiterSteps = [
    ActivateCompanyEnum.ADD_AVATAR,
    ActivateCompanyEnum.RECRUITER_SETTINGS,
    ActivationPageEnum.CONGRATS
  ];

  const companySteps = [
    ActivateCompanyEnum.COMPANY_LOGO,
    ActivateCompanyEnum.ADD_AVATAR,
    ActivateCompanyEnum.NICE_TO_MEET_YOU_FIRST,
    ActivateCompanyEnum.NICE_TO_MEET_YOU_SECOND,
    ActivateCompanyEnum.MAKE_CULTURE_PROFILE,
    ActivationPageEnum.CONGRATS
  ];

  const steps = isRecruiter ? recruiterSteps : companySteps;

  const currentStep =
    steps.indexOf(link as ActivateCompanyEnum | ActivationPageEnum) + 1;
  const totalSteps = steps.length;
  return { currentStep, totalSteps };
};

const ActivationPage: FC = (): JSX.Element => {
  const [link, setLink] = useState<string>(ActivationPageEnum.WELCOME);
  const [contentToShow, setContentToShow] = useState<JSX.Element>();
  const [user, setUser] = useState({
    name: '',
    surname: ''
  });
  const [companyData, setCompanyData] = useState<any>();

  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;
  const navigate = useNavigate();
  const testMode = useActivationTestMode();

  const assessmentByGroup = useSelector(
    (state: any) => state.assessment.assessmentByGroup
  );

  const isRecruiter = testMode.enabled
    ? testMode.flow === 'recruiter'
    : me?.role === 'recruiter';

  const isCompany = testMode.enabled
    ? testMode.flow === 'company'
    : me?.role !== 'candidate' && me?.role !== 'recruiter';

  useEffect(() => {
    if (me) {
      setUser({ name: me.first_name, surname: me.last_name });
    }
  }, [me]);

  useEffect(() => {
    contentToShowHandler();
  }, [link, user, isCompany, isRecruiter, assessmentByGroup]);

  const exitTestActivation = (): void => {
    navigate(TEST_ACTIVATION_ROUTE);
  };

  useEffect(() => {
    if (!assessmentByGroup || assessmentByGroup.length === 0) {
      // setLoadingCompetencies(true);
      JobService.getAssessmentByGroup();
    }
  }, [link]);

  const handleGetStarted = () => {
    if (isRecruiter) {
      setLink(ActivateCompanyEnum.ADD_AVATAR);
    } else if (isCompany) {
      setLink(ActivateCompanyEnum.COMPANY_LOGO);
    } else {
      setLink(ActivationPageEnum.WHO_YOU_ARE);
    }
  };

  const handleOnSubmit = async (skipAssessment?: boolean): Promise<void> => {
    if (testMode.enabled) {
      exitTestActivation();
      return;
    }
    try {
      await handleInfoChange();
      const res = await AuthService.getAccount();
      if (!res.is_new) {
        if (res.role === 'candidate') {
          if (skipAssessment) {
            navigate(CANDIDATE_ROUTES.BASE);
          }
        } else if (
          res.role === 'company' ||
          res.role === 'recruiter' ||
          res.role === 'admin' ||
          res.is_superuser
        ) {
          navigate('/company/find-candidates');
        } else {
          if (skipAssessment) {
            navigate(CANDIDATE_ROUTES.BASE);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnNextPage = (page: string): void => {
    setLink(() => page);
  };

  const handleInfoChange = async () => {
    const body = {
      is_new: false
    };
    const formData = new FormData();
    for (const key in body) {
      if (key !== 'id') {
        formData.append(key, (body as any)[key]);
      }
    }
    try {
      await AccountServce.updateAccount(me.id, formData);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnSubmitCompany = async (): Promise<void> => {
    if (testMode.enabled) {
      exitTestActivation();
      return;
    }
    const avatarFormData = new FormData();
    const companyRequestBody: Record<string, any> = {};

    // 1. Submit name/email if present
    if (companyData?.first_name || companyData?.last_name) {
      const nameFormData = new FormData();
      if (companyData.first_name)
        nameFormData.append('first_name', companyData.first_name);
      if (companyData.last_name)
        nameFormData.append('last_name', companyData.last_name);
      try {
        await AccountServce.updateAccount(me.id, nameFormData);
      } catch (error) {
        console.log(error);
      }
    }
    if (companyData?.email && companyData?.email.length > 0) {
      const emailFormData = new FormData();
      emailFormData.append('email', companyData.email);
      try {
        await AccountServce.updateAccount(me.id, emailFormData);
      } catch (error) {
        console.log(error);
      }
    }
    // 2. Submit password if present
    if (companyData?.newPassword && companyData?.repeatPassword) {
      try {
        await AuthService.changePassword({
          old_password: companyData.oldPassword || '',
          new_password: companyData.newPassword
        });
      } catch (error) {
        console.log(error);
      }
    }

    // 3. Prepare company data
    for (const key in companyData) {
      if (key === 'avatar') {
        avatarFormData.append(key, (companyData as any)[key])
      } else if (key === 'location' && companyData[key]) {
        // Handle location object properly as a regular object
        companyRequestBody.location = {
          id: companyData[key].id,
          display_name: companyData[key].display_name
        };
      } else if (key === 'industry' && companyData[key]) {
        const industry = normalizeIndustry(companyData[key]);
        if (industry) {
          companyRequestBody.industry = industry;
        }
      } else if (key !== 'id' && key !== 'cultureProfile' && key !== 'logo') {
        companyRequestBody[key] = (companyData as any)[key];
      }
    }

    try {
      const accountUpdate = await AccountServce.updateAccount(
        me.id,
        avatarFormData
      );
      
      // Submit company data as regular object (like CompanyInfoPopup does)
      const res = await AuthService.getAccountCompany(
        me.company,
        companyRequestBody
      );

      // Upload logo separately if present
      if (companyData?.logo && res) {
        try {
          await AccountServce.uploadCompanyLogo(me.company, companyData.logo);
        } catch (logoError) {
          console.error('Error uploading company logo:', logoError);
          // Don't fail the entire process if logo upload fails
          // The user can still complete the activation process
        }
      }

      // Submit culture profile if present
      if (companyData?.cultureProfile) {
        await MatchProfileService.createMatchProfile(
          true,
          companyData.cultureProfile
        );
      }

      if (res && accountUpdate) {
        handleOnSubmit();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnSubmitRecruiter = async (): Promise<void> => {
    if (testMode.enabled) {
      exitTestActivation();
      return;
    }
    const recruiterFormData = new FormData();
    if (companyData?.first_name)
      recruiterFormData.append('first_name', companyData.first_name);
    if (companyData?.last_name)
      recruiterFormData.append('last_name', companyData.last_name);
    if (companyData?.job_title)
      recruiterFormData.append('job_title', companyData.job_title);
    if (companyData?.email && companyData?.email.length > 0)
      recruiterFormData.append('email', companyData.email);
    if (companyData?.avatar)
      recruiterFormData.append('avatar', companyData.avatar);
    recruiterFormData.append('is_new', 'false');

    // Handle password change separately
    if (companyData?.newPassword && companyData?.repeatPassword) {
      try {
        await AuthService.changePassword({
          old_password: companyData.oldPassword || '',
          new_password: companyData.newPassword
        });
      } catch (error) {
        console.log(error);
      }
    }

    try {
      await AccountServce.updateAccount(me.id, recruiterFormData);
      await AuthService.getAccount();

      navigate('/company/find-candidates');
    } catch (error) {
      console.log(error);
    }
  };

  const contentToShowHandler = (): void => {
    const { currentStep, totalSteps } = getStepInfo(link, isRecruiter);

    switch (link) {
      case ActivateCompanyEnum.COMPANY_LOGO:
        setContentToShow(
          <ActivationCompanyLogo
            setCompanyData={setCompanyData}
            t={t}
            onSubmit={() => handleOnNextPage(ActivateCompanyEnum.ADD_AVATAR)}
            onBack={() => handleOnNextPage(ActivateCompanyEnum.WELCOME)}
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        );
        break;
      case ActivateCompanyEnum.ADD_AVATAR:
        setContentToShow(
          <ActivationAvatarLogo
            setCompanyData={setCompanyData}
            t={t}
            onSubmit={() =>
              isRecruiter
                ? handleOnNextPage(ActivateCompanyEnum.RECRUITER_SETTINGS)
                : handleOnNextPage(ActivateCompanyEnum.NICE_TO_MEET_YOU_FIRST)
            }
            onBack={() =>
              isRecruiter
                ? handleOnNextPage(ActivationPageEnum.WELCOME)
                : handleOnNextPage(ActivateCompanyEnum.COMPANY_LOGO)
            }
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        );
        break;
      case ActivateCompanyEnum.NICE_TO_MEET_YOU_FIRST:
        setContentToShow(
          <CompanyInfo
            setCompanyData={setCompanyData}
            onBack={() => handleOnNextPage(ActivateCompanyEnum.ADD_AVATAR)}
            onSubmit={() =>
              handleOnNextPage(ActivateCompanyEnum.NICE_TO_MEET_YOU_SECOND)
            }
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        );
        break;
      case ActivateCompanyEnum.NICE_TO_MEET_YOU_SECOND:
        setContentToShow(
          <CompanyInfoSecond
            onBack={() =>
              handleOnNextPage(ActivateCompanyEnum.NICE_TO_MEET_YOU_FIRST)
            }
            setCompanyData={setCompanyData}
            onSubmit={() => {
              if (isRecruiter) {
                handleOnNextPage(ActivateCompanyEnum.RECRUITER_SETTINGS);
              } else {
                handleOnNextPage(ActivateCompanyEnum.MAKE_CULTURE_PROFILE);
              }
            }}
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        );
        break;
      case ActivateCompanyEnum.RECRUITER_SETTINGS:
        setContentToShow(
          <RecruiterSettingsStep
            t={t}
            onBack={() =>
              isRecruiter
                ? handleOnNextPage(ActivateCompanyEnum.ADD_AVATAR)
                : handleOnNextPage(ActivateCompanyEnum.NICE_TO_MEET_YOU_SECOND)
            }
            onSubmit={() =>
              isRecruiter
                ? handleOnNextPage(ActivationPageEnum.CONGRATS)
                : handleOnNextPage(ActivateCompanyEnum.MAKE_CULTURE_PROFILE)
            }
            setCompanyData={setCompanyData}
            companyData={companyData}
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        );
        break;
      case ActivateCompanyEnum.MAKE_CULTURE_PROFILE:
        setContentToShow(
          !assessmentByGroup || assessmentByGroup.length === 0 ? (
            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              {t('Loading competencies...')}
            </div>
          ) : (
            <ActivationCultureProfile
              competencies={assessmentByGroup}
              onComplete={({ title, competencies }) => {
                setCompanyData((prev: any) => ({
                  ...prev,
                  cultureProfile: {
                    title,
                    is_culture: true,
                    competencies: competencies.map((c) => ({
                      competency: c.id,
                      min_score: c.score ?? 5
                    }))
                  }
                }));
                handleOnNextPage(ActivateCompanyEnum.CONGRATS);
              }}
              onBack={() =>
                isRecruiter
                  ? handleOnNextPage(ActivateCompanyEnum.RECRUITER_SETTINGS)
                  : handleOnNextPage(
                      ActivateCompanyEnum.NICE_TO_MEET_YOU_SECOND
                    )
              }
              currentStep={currentStep}
              totalSteps={totalSteps}
            />
          )
        );
        break;
      case ActivationPageEnum.GET_STARTED:
        setContentToShow(<ReadyToStart t={t} />);
        break;
      case ActivationPageEnum.WHO_YOU_ARE:
        setContentToShow(
          <WhoYouAre
            t={t}
            onSubmit={() =>
              handleOnNextPage(ActivationPageEnum.MATCH_PREFERENCES)
            }
          />
        );
        break;
      case ActivationPageEnum.MATCH_PREFERENCES:
        setContentToShow(<MatchPreferences t={t} onClick={handleOnNextPage} />);
        break;
      case ActivationPageEnum.EDUCATION:
        setContentToShow(
          <Education
            educationClassName="edu-activation"
            t={t}
            onClick={handleOnNextPage}
          />
        );
        break;
      case ActivationPageEnum.EDUCATION_VIEW:
        setContentToShow(<EducationView t={t} onClick={handleOnNextPage} />);
        break;
      case ActivationPageEnum.CONGRATS:
        setContentToShow(
          testMode.enabled ? (
            <TestPayloadPreview
              me={me}
              isCompany={isCompany}
              isRecruiter={isRecruiter}
              companyData={companyData}
            />
          ) : (
            <Congratulation
              t={t}
              onSubmit={(skipAssessment) => {
                if (isCompany) {
                  handleOnSubmitCompany();
                } else if (isRecruiter) {
                  handleOnSubmitRecruiter();
                } else {
                  handleOnSubmit(skipAssessment);
                }
              }}
              isCompany={isCompany}
              isRecruiter={isRecruiter}
              me={me}
            />
          )
        );
        break;
      case ActivationPageEnum.WELCOME:
      default:
        setContentToShow(
          <ActivationWelcome
            title={`${t('Welcome')}, ${user?.name} ${user.surname}`}
            message={t("We're thrilled to have you here! Just a few quick steps to set up your account, and you’re ready to go!")}
            onGetStarted={handleGetStarted}
            t={t}
          />
        );
    }
  };

  return (
    <>
      <PageHeader showLogo={false} showLanguageSelector={false} />
      {testMode.enabled && <TestActivationBanner />}
      <ActivationFlowProvider>
        <div className="activation-wrapper">
          <div className="activation-pref">{contentToShow}</div>
        </div>
      </ActivationFlowProvider>
    </>
  );
};

export default ActivationPage;
